import { collapsibleText } from './prerenderText';

/**
 * Save a prerendered page as React first rendered it, not as it ended up.
 *
 * Hydration compares the static HTML with React's FIRST render. react-snap saves
 * the page after every effect has run: data fetched (or failed, which shows a
 * fallback), media queries applied at its own 480px viewport, canvases sized,
 * panels mounted after load. On / each of those was a mismatch (#418), so React
 * threw the static HTML away and re-rendered the whole root, and the visitor saw
 * the 480px snapshot, then the first render, then the settled page.
 *
 * captureFirstRender(container) keeps a deep copy of each node React inserts into
 * the container during its first commit. That is the mutation phase, before any
 * layout or passive effect has run, so the copies are exactly the first render.
 * It returns a function that puts those copies back in place of whatever the
 * page has become. src/index.js calls it from window.snapSaveState, so the saved
 * HTML is what hydration expects, at every viewport. The visitor's settled page
 * is unchanged: the same effects run after hydration as after a client render.
 *
 * Text-node boundaries are a separate problem, handled by
 * utils/prerenderText's separateAdjacentText.
 */
export const captureFirstRender = (container) => {
  const copies = [];
  let open = true;
  const proto = Object.getPrototypeOf(container);
  const record = (node) => {
    if (!open) return;
    // The whole first commit is synchronous; anything after it is not the first render.
    if (!copies.length) {
      queueMicrotask(() => {
        open = false;
        delete container.appendChild;
        delete container.insertBefore;
      });
    }
    copies.push(node.cloneNode(true));
  };
  container.appendChild = function appendChild(node) {
    record(node);
    return proto.appendChild.call(this, node);
  };
  container.insertBefore = function insertBefore(node, before) {
    record(node);
    return proto.insertBefore.call(this, node, before);
  };
  return () => {
    if (!copies.length) return false;
    container.replaceChildren(...copies);
    return true;
  };
};

/**
 * Why a restored first render must not ship. Either one makes the snapshot
 * wrong while the page still looks plausible: a loading spinner saved as a
 * hydrating page (its route chunk was not loaded before the first render, see
 * utils/lazyPage), or text the minifier rewrites, which fails hydration
 * (utils/prerenderText). Returns a description of each, empty when there are none.
 */
export const firstRenderHazards = (root) => [
  ...(root.querySelector('[data-page-fallback]') ? ['the first render was the page loading spinner'] : []),
  ...collapsibleText(root).map((text) => `whitespace the minifier collapses in ${JSON.stringify(text.slice(0, 60))}`),
];

export default captureFirstRender;
